import { formatCurrency } from './format'
import { isNullOrUndefined } from './functions'
import { isNotNullOrUndefined } from './isNotNullOrUndefined'

export const STALE_UNIT_PERCENT_ADJUSTMENT_AMOUNT_MAX = 100

/** Dollar vs percent, matching `StaleUnitRentAdjustmentType` on Property. */
export type StaleUnitRentAdjustmentType = 'dollar' | 'percent'

/**
 * Shown wherever a percent adjustment outside the allowed range is rejected: the amount input, the
 * bulk CSV import, and the GraphQL mutation. The number the user works with is always the positive
 * magnitude, which is why the message does not mention negative values.
 */
export const staleUnitPercentAdjustmentAmountErrorMessage = 'Enter a value from 0 to 100'
export const staleUnitRentAdjustmentAmountMagnitudeMinMessage = 'Enter a value of 0 or more'

/**
 * An amount on its own does not say whether it means dollars or a percentage, and the feature will
 * not guess, so the pair is rejected rather than saved in a state that silently does nothing.
 */
export const staleUnitRentAdjustmentTypeRequiredMessage =
  'Stale unit rent adjustment type is required when an amount is set'

export const staleUnitRentAdjustmentAmountMustBeDiscountMessage =
  'Stale unit rent adjustment amount must be a discount or 0'

export const staleUnitDollarAdjustmentAmountErrorMessage =
  'Stale unit rent adjustment amount must be a whole-dollar amount'

export const staleUnitRentAdjustmentDaysThresholdMinMessage =
  'Stale unit rent adjustment vacant ready days threshold must be at least 1'

/**
 * Shown when the amount input is submitted with a magnitude of 0. An amount of 0 is allowed and is
 * saved, so this warns about what it will do instead of rejecting it.
 */
export const staleUnitRentAdjustmentAmountZeroWarningMessage =
  'Setting the amount to 0 will apply a 0 value adjustment and remove any existing unit rent adjustment. If you want to disable staleness adjustments, toggle the setting off.'

export const staleUnitRentAdjustmentAmountFormatDescription =
  'must be a whole-dollar amount such as -$50 or a percentage such as -5%'

export const staleUnitRentAdjustmentTypeToLabel: Record<StaleUnitRentAdjustmentType, string> = {
  dollar: 'By dollar',
  percent: 'By percent',
}

/**
 * Validates the positive magnitude shown in the adjustment amount input when the type is percent.
 * The stored value is the negated magnitude (e.g. input 7 is saved as -7%).
 */
export function getStaleUnitPercentAdjustmentAmountInputError(value: number): string | null {
  if (value >= STALE_UNIT_PERCENT_ADJUSTMENT_AMOUNT_MAX) {
    return staleUnitPercentAdjustmentAmountErrorMessage
  }
  return null
}

/** Validates the positive magnitude shown in the dollar or percent adjustment amount input. */
export function getStaleUnitAdjustmentAmountInputError(
  value: number,
  type: StaleUnitRentAdjustmentType
): string | null {
  if (value < 0) {
    return staleUnitRentAdjustmentAmountMagnitudeMinMessage
  }
  return type === 'percent' ? getStaleUnitPercentAdjustmentAmountInputError(value) : null
}

/**
 * A percent adjustment must leave some rent in place, so the stored amount must be closer to zero
 * than -100.
 */
export function isStaleUnitPercentAdjustmentAmountInRange(amount: number | null): boolean {
  return amount === null || Math.abs(amount) < STALE_UNIT_PERCENT_ADJUSTMENT_AMOUNT_MAX
}

/** The stored fields, as they appear on a property or on an update to one. */
export interface StaleUnitRentAdjustmentFields {
  staleUnitRentAdjustmentType?: string | null
  staleUnitRentAdjustmentAmount?: number | null
  staleUnitRentAdjustmentDaysSinceAvailableThreshold?: number | null
}

/** Whether an update touches a field that needs validating. */
export function doesUpdateChangeStaleUnitRentAdjustment(
  update: StaleUnitRentAdjustmentFields
): boolean {
  return (
    update.staleUnitRentAdjustmentType !== undefined ||
    update.staleUnitRentAdjustmentAmount !== undefined ||
    update.staleUnitRentAdjustmentDaysSinceAvailableThreshold !== undefined
  )
}

/**
 * Validates a type, amount, and threshold that are stored together. Returns the error to show the
 * user, or null when the combination is allowed.
 */
export function getStaleUnitRentAdjustmentError(
  fields: StaleUnitRentAdjustmentFields
): string | null {
  const threshold = fields.staleUnitRentAdjustmentDaysSinceAvailableThreshold
  if (isNotNullOrUndefined(threshold) && threshold < 1) {
    return staleUnitRentAdjustmentDaysThresholdMinMessage
  }

  const amount = fields.staleUnitRentAdjustmentAmount
  if (isNotNullOrUndefined(amount) && amount > 0) {
    return staleUnitRentAdjustmentAmountMustBeDiscountMessage
  }
  if (isNullOrUndefined(fields.staleUnitRentAdjustmentType) && isNotNullOrUndefined(amount)) {
    return staleUnitRentAdjustmentTypeRequiredMessage
  }
  if (
    fields.staleUnitRentAdjustmentType === 'dollar' &&
    isNotNullOrUndefined(amount) &&
    !Number.isInteger(amount)
  ) {
    return staleUnitDollarAdjustmentAmountErrorMessage
  }
  if (fields.staleUnitRentAdjustmentType !== 'percent') {
    return null
  }
  return isStaleUnitPercentAdjustmentAmountInRange(fields.staleUnitRentAdjustmentAmount ?? null)
    ? null
    : staleUnitPercentAdjustmentAmountErrorMessage
}

/**
 * Validates the pair against the values the property will hold once the update is applied.
 * An update may set only one of the fields, so the others are read from the property as it is
 * stored today.
 */
export function getStaleUnitRentAdjustmentUpdateError({
  update,
  storedProperty,
}: {
  update: StaleUnitRentAdjustmentFields
  storedProperty: StaleUnitRentAdjustmentFields
}): string | null {
  if (!doesUpdateChangeStaleUnitRentAdjustment(update)) {
    return null
  }
  return getStaleUnitRentAdjustmentError({
    staleUnitRentAdjustmentType:
      update.staleUnitRentAdjustmentType !== undefined
        ? update.staleUnitRentAdjustmentType
        : storedProperty.staleUnitRentAdjustmentType,
    staleUnitRentAdjustmentAmount:
      update.staleUnitRentAdjustmentAmount !== undefined
        ? update.staleUnitRentAdjustmentAmount
        : (storedProperty.staleUnitRentAdjustmentAmount ?? null),
    staleUnitRentAdjustmentDaysSinceAvailableThreshold:
      update.staleUnitRentAdjustmentDaysSinceAvailableThreshold !== undefined
        ? update.staleUnitRentAdjustmentDaysSinceAvailableThreshold
        : (storedProperty.staleUnitRentAdjustmentDaysSinceAvailableThreshold ?? null),
  })
}

/**
 * Formats a stored stale unit adjustment for the property settings export, combining the type and
 * the amount into a single value such as `-$50` or `-7.5%`. Returns an empty string when no amount
 * is set.
 */
export function formatStaleUnitRentAdjustmentAmount({
  amount,
  type,
}: {
  amount: number | null | undefined
  type: string | null | undefined
}): string {
  if (isNullOrUndefined(amount)) {
    return ''
  }
  const sign = amount < 0 ? '-' : ''
  const magnitude = Math.abs(amount)
  return type === 'percent' ? `${sign}${magnitude}%` : `${sign}$${magnitude}`
}

/**
 * Same as {@link formatStaleUnitRentAdjustmentAmount}, but `null` when no amount is set so
 * confirmation UI can show “None” and “Removed”.
 */
export function formatStaleUnitRentAdjustmentAmountForDisplay(input: {
  amount: number | null | undefined
  type: string | null | undefined
}): string | null {
  const formatted = formatStaleUnitRentAdjustmentAmount(input)
  return formatted === '' ? null : formatted
}

export type ParsedStaleUnitRentAdjustmentAmount =
  | {
      isValid: true
      type: StaleUnitRentAdjustmentType | null
      /** The stored amount: a negative discount, `0`, or null when the cell was empty. */
      amount: number | null
    }
  | {
      isValid: false
      /**
       * Describes why the value could not be parsed, phrased to follow a
       * "Value for <column name> " prefix.
       */
      reason: string
    }

/**
 * Converts the positive magnitude shown in the amount input (or parsed from a cell) into the signed
 * value stored on the property. Zero is stored as `0`, not negative zero.
 */
export function toStoredStaleUnitRentAdjustmentAmount(magnitude: number): number {
  if (magnitude === 0) {
    return 0
  }
  return -Math.abs(magnitude)
}
const DOLLAR_AMOUNT_PATTERN = /^-?\$(\d+(?:\.\d+)?)$/
const PERCENT_AMOUNT_PATTERN = /^-?(\d+(?:\.\d+)?)%$/

/**
 * Parses the combined type and amount value produced by
 * {@link formatStaleUnitRentAdjustmentAmount} back into the two stored fields.
 */
export function parseStaleUnitRentAdjustmentAmount(
  value: unknown
): ParsedStaleUnitRentAdjustmentAmount {
  if (isNullOrUndefined(value) || (typeof value === 'string' && value.trim() === '')) {
    return { isValid: true, type: null, amount: null }
  }
  if (typeof value !== 'string') {
    return {
      isValid: false,
      reason: `${staleUnitRentAdjustmentAmountFormatDescription}, but got ${String(value)}`,
    }
  }
  const trimmedValue = value.trim()
  const dollarMatch = DOLLAR_AMOUNT_PATTERN.exec(trimmedValue)
  if (dollarMatch) {
    const magnitude = Number(dollarMatch[1])
    if (!Number.isInteger(magnitude)) {
      return {
        isValid: false,
        reason: staleUnitRentAdjustmentAmountFormatDescription,
      }
    }
    return {
      isValid: true,
      type: 'dollar',
      amount: toStoredStaleUnitRentAdjustmentAmount(magnitude),
    }
  }
  const percentMatch = PERCENT_AMOUNT_PATTERN.exec(trimmedValue)
  if (percentMatch) {
    return {
      isValid: true,
      type: 'percent',
      amount: toStoredStaleUnitRentAdjustmentAmount(Number(percentMatch[1])),
    }
  }
  return {
    isValid: false,
    reason: `${staleUnitRentAdjustmentAmountFormatDescription}, but got ${trimmedValue}`,
  }
}

/**
 * Formats a stored stale unit adjustment amount for action history. Type comes from the updater
 * when it was saved with the amount, otherwise from the type recorded on the action, otherwise
 * from the property as it is stored today.
 */
export function formatStaleUnitRentAdjustmentHistoryAmount({
  amount,
  typeFromUpdater,
  typeAtTimeOfUpdate,
  typeOnProperty,
}: {
  amount: number
  typeFromUpdater: string | null | undefined
  typeAtTimeOfUpdate: string | null | undefined
  typeOnProperty: string | null | undefined
}): string {
  const type = typeFromUpdater ?? typeAtTimeOfUpdate ?? typeOnProperty
  if (type === 'percent') {
    return amount > 0 ? `+${amount}%` : `${amount}%`
  }
  return formatCurrency(amount, 0, false, true)
}
